import React, { ReactNode, useState } from 'react';
import { ModalsKeys } from 'global';
import { useSelector } from 'react-redux';
import { Box, Button, Stack } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { generateEquipmentName } from 'modules/equipment';
import { creditAppSelectors } from 'redux/creditAppReducer';
import { ModalsContainer } from 'components/shared/Modals';
import { useHistory, useLocation } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { CreateProposalDTO, useProposal } from 'modules/proposals';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  CreateProposalFinanceExistingOptions,
  DeskingEquipmentFormSection,
  FinanceStructure,
} from 'modules/desking/shared';

import { useCalculate } from '../api';
import { TabsInSection } from './TabsInSection';
import { AddContingencyDialog } from './dialogs';
import { lenderDeskingDataShema } from '../model';
import { lenderDeskingPanelMapper } from '../mappers';
import { ActionsSection, PaymentOptionsSection, EligibleFinanceProgramsSection } from './sections';
import { StyledLenderDeskingPanel } from './StyledLenderDeskingPanel';
import { useCreditSubmission } from '../../hooks/useCreditSubmission';
import { DecisionSection } from './sections/decision/DecisionSection';
import { LenderDeskingPanelApiContainer } from './LenderDeskingPanelApiContainer';
import { FormSectionHeader } from '../../../../components/shared/FormSectionHeader';
import { LenderDeskingPanelFormValues, LenderDeskingPanelProps, LenderDeskingSections } from '../types';
import { useModal } from '../../../../hooks/useModal';

const initialSectionView = Object.values(LenderDeskingSections).reduce(
  (acc, section) => {
    acc[section] = { isSectionOpen: true, isSectionCollapsed: true };
    return acc;
  },
  {} as Record<LenderDeskingSections, { isSectionOpen: boolean; isSectionCollapsed: boolean }>
);

const modalsWithKeys = {
  [ModalsKeys.AddContingencyDialog]: <AddContingencyDialog />,
};

export const LenderDeskingPanel = ({
  vo,
  cs,
  invoice,
  equipment,
  accountId,
  dealerEmail,
  creditAppData,
  currentUserEmail,
  preSelectedValue,
  availableEmailOptions,
}: LenderDeskingPanelProps) => {
  const { replace } = useHistory();
  const location = useLocation();
  const { handleOpen: handleOpenConfirmationModal } = useModal(ModalsKeys.Confirmation);

  const customerData = useSelector(creditAppSelectors.customerData);

  const { handleCreateProposalOnSubmissionApproval, isProposalCreating } = useProposal();
  const { handlePostCSDecision, isCSDecisionPosting } = useCreditSubmission();

  const [sectionView, setSectionView] = useState(initialSectionView);

  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);

  const form = useForm<LenderDeskingPanelFormValues>({
    mode: 'all',
    resolver: zodResolver(lenderDeskingDataShema),
    defaultValues: lenderDeskingPanelMapper.initPanel({ invoice, equipment, cs, preSelectedValue }),
  });

  const [program, decision] = useWatch({
    control: form.control,
    name: ['financeQuote', 'submission.decision'],
  });

  const { isCalculating, financePricing, setFinancePricing } = useCalculate(program, cs, invoice);

  const handleCreateProposalAndUpdateCS = async (
    proposalDTO: Omit<CreateProposalDTO, 'vendorOpportunityId' | 'creditSubmissionId'>,
    csDTO: Record<string, any>,
    notification?: { recipient: string; internalEmails: string[] }
  ) => {
    const isCSDecisionApproved = cs?.decision === 'Approved';

    await Promise.all([
      handlePostCSDecision(cs?.creditSubId, {
        masterCreditSubmissionId: cs.masterCreditSubmissionId ?? null,
        ...(!isCSDecisionApproved ? { decision: Number(decision) } : {}),
        ...csDTO,
      }),

      handleCreateProposalOnSubmissionApproval(
        {
          vendorOpportunityId: vo.vendorOpportunityId,
          creditSubmissionId: cs?.creditSubmissionId,
          ...proposalDTO,
        },
        notification
      ),
    ]).then(() => {
      replace({ hash: '#proposals', pathname: location.pathname });
    });
  };

  const handleSaveAsDraft = async (formValues: LenderDeskingPanelFormValues) => {
    const createAsDraftDTO = lenderDeskingPanelMapper.processFormToCreateDto({
      invoice,
      formValues,
      isDraft: true,
      financePricing,
      creditAppData,
      customerData,
    });

    await handleCreateProposalAndUpdateCS(createAsDraftDTO, {
      rate: formValues.financeQuote.commonRate,
      notes: formValues.submission.noteToBuyer,
      approvedAmount: formValues.financeQuote.maxApprovalAmount,
    });
  };

  const handleSendToDealer = async (formValues: LenderDeskingPanelFormValues) => {
    const ccEmails: Partial<Record<'lender' | 'dealer', string>> = {
      lender: currentUserEmail,
      ...(dealerEmail ? { dealer: dealerEmail } : {}),
    };

    const createSendToDealerDTO = lenderDeskingPanelMapper.processFormToCreateDto({
      invoice,
      ccEmails,
      formValues,
      creditAppData,
      isDraft: false,
      financePricing,
      customerData,
    });

    await handleCreateProposalAndUpdateCS(
      createSendToDealerDTO,
      {
        rate: formValues.financeQuote.commonRate,
        notes: formValues.submission.noteToBuyer,
        approvedAmount: formValues.financeQuote.maxApprovalAmount,
      },
      { recipient: 'dealer', internalEmails: formValues.actions.notifyUsers.map(({ email }) => email) }
    );
  };

  const handleSendToApplicant = async (formValues: LenderDeskingPanelFormValues) => {
    const createSendToApplicantDTO = lenderDeskingPanelMapper.processFormToCreateDto({
      invoice,
      formValues,
      creditAppData,
      isDraft: false,
      financePricing,
      customerData,
    });

    await handleCreateProposalAndUpdateCS(
      createSendToApplicantDTO,
      {
        rate: formValues.financeQuote.commonRate,
        notes: formValues.submission.noteToBuyer,
        approvedAmount: formValues.financeQuote.maxApprovalAmount,
      },
      { recipient: 'applicant', internalEmails: formValues.actions.notifyUsers.map(({ email }) => email) }
    );
  };

  const handleResetStructure = () => {
    setFinancePricing({});
    form.reset();
  };

  const isAllActionsLoading = isCSDecisionPosting || isProposalCreating;

  return (
    <>
      <LenderDeskingPanelApiContainer accountId={accountId}>
        {({ loading, financeProgramOptions, actions }) => {
          const handleApplyProgram = async (financeProgramId: string, tierName: string | null) => {
            const scoreCardResult = tierName ? { highestTierPassed: tierName } : cs?.scoreCardResult;
            const formValues = await actions.handleFetchFinanceProgram(financeProgramId, scoreCardResult);

            if (formValues) {
              Object.entries(formValues).forEach(([key, fieldValue]) => {
                form.setValue(`financeQuote.${key}` as any, fieldValue);
              });

              form.setValue('financeQuote.program', {
                label: financeProgramOptions.find(o => o.value === financeProgramId)?.label ?? '',
                value: financeProgramId,
              });

              form.setValue('financeQuote.createProposalWithExistingProgram', CreateProposalFinanceExistingOptions.Yes);
            }
          };

          const renderNestedSectionByMode: Record<string, ReactNode> = {
            financeProgram: (
              <FinanceStructure
                financeAmount={invoice.invoiceTotal}
                financeProgramOptions={financeProgramOptions}
                isFPOptionsLoading={loading.isFPOptionsLoading}
                maxApprovalAmount={program.maxApprovalAmount ?? 0}
                netInvoiceAmount={Number(form.getValues('equipment.totalWholesaleCost'))}
                onFetchFinanceProgram={async programId =>
                  actions.handleFetchFinanceProgram(programId, cs?.scoreCardResult)
                }
              />
            ),
            conditions: null,
          };

          return (
            <StyledLenderDeskingPanel>
              <FormProvider {...form}>
                <DecisionSection value={LenderDeskingSections.DECISION} />
                <RenderWithCondition condition={decision === '803370003'}>
                  <EligibleFinanceProgramsSection
                    vo={vo}
                    accountId={accountId}
                    creditSubmissionId={cs?.creditSubmissionId}
                    onApplyProgram={handleApplyProgram}
                  />

                  <RenderWithCondition condition={!!equipment.length}>
                    <DeskingEquipmentFormSection
                      name="Equipment"
                      isSectionDisabled
                      equipments={equipment}
                      value={LenderDeskingSections.EQUIPMENT}
                      isSectionOpen={sectionView[LenderDeskingSections.EQUIPMENT].isSectionCollapsed}
                      handleUpdateOpenStatus={(section, nextValue) => {
                        setSectionView(prev => ({
                          ...prev,
                          [section]: { ...prev[section], isSectionCollapsed: nextValue },
                        }));
                      }}
                      equipmentOptions={equipment.map(equipmentItem => ({
                        value: equipmentItem.equipmentId,
                        label: generateEquipmentName(equipmentItem),
                      }))}
                    />
                  </RenderWithCondition>

                  <Box className="row">
                    <Stack gap="0.5rem">
                      <FormSectionHeader
                        name="Structure"
                        value={LenderDeskingSections.STRUCTURE}
                        extraAction={
                          <Button size="small" onClick={handleResetStructure}>
                            Reset
                          </Button>
                        }
                      />

                      <TabsInSection
                        renderNestedSectionByMode={renderNestedSectionByMode}
                        configuration={[
                          { key: 'financeProgram', label: 'Finance' },
                          { key: 'conditions', label: 'Conditions', disabled: true },
                        ]}
                      />
                    </Stack>

                    <PaymentOptionsSection
                      selectedTerm={selectedTerm}
                      isCalculating={isCalculating}
                      financePricing={financePricing}
                      onSelectOption={term => setSelectedTerm(prev => (prev === term ? null : term))}
                    />
                  </Box>

                  <ActionsSection
                    name="Send"
                    value={LenderDeskingSections.SEND}
                    availableEmailOptions={availableEmailOptions}
                    actions={{
                      cancelChanges: {
                        isDisabled: isAllActionsLoading,
                        fn: () =>
                          handleOpenConfirmationModal({
                            title: 'Confirmation',
                            onConfirm: handleResetStructure,
                            message: 'Are you sure? All changes will be reset!',
                          }),
                      },
                      sendToDealer: {
                        isLoading: isAllActionsLoading,
                        fn: () => form.handleSubmit(handleSendToDealer)(),
                      },
                      saveAsDraft: {
                        isLoading: isAllActionsLoading,
                        fn: () => form.handleSubmit(handleSaveAsDraft)(),
                      },
                      sendToApplicant: {
                        isLoading: isAllActionsLoading,
                        fn: () => form.handleSubmit(handleSendToApplicant)(),
                      },
                    }}
                  />
                </RenderWithCondition>
              </FormProvider>
            </StyledLenderDeskingPanel>
          );
        }}
      </LenderDeskingPanelApiContainer>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </>
  );
};
